// Single source of truth for the self-serve Agency Tools plans.
//
// Both the pricing cards (AgencyPricingPlans) and the feature comparison
// table (PricingComparisonTable) read these definitions so the two can
// never drift apart. This data is authoritative for what we DISPLAY and
// must mirror `supabase/functions/_shared/razorpay-agency-plans.ts` (the
// server-side source of truth for what Razorpay actually charges).
//
// Kept in its own non-component module so Vite Fast Refresh stays happy
// (component files should only export components).

export type Cycle = 'monthly' | 'yearly';

// Feature switch for the monthly/yearly toggle. HIDDEN from regular
// users — the marketing surfaces (pricing cards, comparison table,
// pricing FAQ, in-app billing tab) all render monthly-only while this
// is false. The Razorpay yearly plan IDs in razorpay-agency-plans.ts
// stay provisioned, and existing yearly subscribers (if any) still
// render correctly on their billing tab (the AgencyBillingTab cycle
// default reads from their subscription row, not this flag). To
// re-enable yearly publicly, flip this AND the matching flag in
// AgencyBillingTab.tsx — they share the same gate.
export const YEARLY_ENABLED = false;

// The free offer: 25 scans, valid 14 days from signup.
//
// Kept as constants because several surfaces quote them (the Starter card's
// trial CTA, the tools page, the welcome email). The number this replaced was
// 1,500 credits that never expired, and it had already drifted: the tools page
// said 5,000 while the pricing card beside it said 1,500. The authoritative
// write is grant_agency_signup_trial in the database; these exist only to say
// the same thing on screen.
export const FREE_TRIAL_SCANS = 25;
export const FREE_TRIAL_DAYS = 14;

export interface TierCard {
  slug: 'starter' | 'pro';
  name: string;
  tagline: string;
  /**
   * The card's credit allowance as a number: monthly on the recurring tiers,
   * a single grant on Lifetime.
   *
   * Exists so the card can say what the allowance actually BUYS ("about 45
   * Google profile audits") via headlineRunsLabel in
   * src/config/toolCreditCosts.ts, whose costs are test-bound to the server
   * file that charges. Deriving it means the runs figure can never go stale
   * the day a tool's price moves; hardcoding a second number would.
   *
   * Must match the credits figure written into highlights[0] on the same
   * tier, and, like it, mirrors razorpay-agency-plans.ts.
   */
  credits: number;
  /**
   * Recurring prices, driven by the monthly/yearly toggle.
   *
   * OPTIONAL because a one-time plan has neither. Lifetime is charged once
   * and never renews, so putting 99 in monthly_usd would be a lie the card
   * renderer would happily print as "$99/mo". One-time plans leave these
   * undefined and set one_time_usd + cycle: 'one_time' instead.
   */
  monthly_usd?: number;
  yearly_usd?: number;
  /** Whole-USD price of a one-time plan. Set only alongside
   *  cycle: 'one_time'. Mirrors LIFETIME_99.usd on the server. */
  one_time_usd?: number;
  /**
   * How the card is charged. Absent (the default) means a recurring plan
   * that follows the monthly/yearly toggle. 'one_time' means charged once:
   * the card reads one_time_usd, renders a one-time price suffix instead of
   * "/mo", never shows the yearly note, and sends cycle=none to checkout
   * (the value a non-recurring purchase carries end to end).
   */
  cycle?: 'one_time';
  /**
   * Feature bullets rendered on the pricing card, top-to-bottom.
   * Single source of truth — the Product Hunt landing page and the
   * /pricing page read THIS list, so the two stay aligned. Only list
   * what actually ships today; phantom features (custom CRM hooks,
   * multi-domain embeds, etc.) get cut, not promised.
   */
  highlights: string[];
  highlight?: boolean;
  ctaLabel: string;
  /** When set, the card surfaces a secondary CTA above the primary one
   *  that routes the user into the 7-day free trial flow (no payment).
   *  Currently lives only on the Starter tile — the trial is "try
   *  Starter for free" semantically, so it's offered there instead of
   *  as a standalone tile. */
  trialCtaLabel?: string;
  /** When set, clicking the CTA routes to this href instead of the
   *  embed-inquiry modal. Used for the future signup flow. */
  ctaHref?: string;
}

// Note: there is no standalone Free / 7-day trial tier in this array.
// Earlier versions had one; we collapsed it into Starter because the
// trial is semantically "try Starter for 7 days" — keeping it as a
// separate tile created a phantom plan that no one actually subscribes
// to. The trial CTA now lives ON the Starter card via trialCtaLabel.
//
// Enterprise was also removed — it was a "contact us" tile with no
// matching Razorpay plan (razorpay-agency-plans.ts excludes 'enterprise'
// from AGENCY_PLANS) and the promises on it ("native CRM", "per-
// workspace branding") don't ship today. The pricing page's closing
// CTA still surfaces a "Talk to our team" path for buyers who outgrow
// Scale; that's enough until the real Enterprise feature set lands.
// Two plans, priced in scans. One tool run costs one scan, whatever the tool,
// except AI Search Visibility at 7 because it queries several models per run.
//
// The unit is the point. Credits could not be reasoned about: a run cost
// anywhere between 10 and 1,200 of them depending on which tool you picked, so
// "5,000 credits" told an agency nothing about how much work they could do. A
// scan is one audit, so the number on the card is the number of audits.
//
// The free offer is not a tier here. It is a 14-day trial of 25 scans, granted
// automatically at signup, surfaced as the secondary CTA on Starter.
export const TIERS: TierCard[] = [
  {
    slug: 'starter',
    name: 'Starter',
    tagline: 'Solo agencies + freelancers',
    credits: 100,
    monthly_usd: 9.99,
    yearly_usd: 99,
    highlights: [
      '100 scans per month',
      'All audit tools unlocked',
      'Logo + 1 brand colour',
      'Email + dashboard delivery',
      'Email support (72h)',
    ],
    ctaLabel: 'Get Starter',
    trialCtaLabel: 'Start 14-day free trial',
  },
  {
    slug: 'pro',
    name: 'Pro',
    tagline: 'Agencies running client funnels',
    credits: 300,
    monthly_usd: 19.99,
    yearly_usd: 199,
    highlights: [
      '300 scans per month',
      'All audit tools unlocked',
      'White-label reports + custom domain',
      'Webhook + CSV export',
      'Priority support (24h)',
    ],
    highlight: true,
    ctaLabel: 'Get Pro',
  },
];

// `undefined` is accepted alongside null because the recurring price fields are
// optional now (a one-time plan has no monthly_usd). Both mean "no price on
// this axis", so both render the same way rather than printing "$undefined".
export function formatPrice(usd: number | null | undefined): string {
  if (usd === null || usd === undefined) return 'Custom';
  if (usd === 0) return '$0';
  return `$${usd}`;
}

// Comment marker for the comparison table: it used to read
// `credits` / `whiteLabel` / `leadDelivery` / `support` as rows. Those
// fixed slots were retired in favour of free-form `highlights: string[]`
// so each tier can list only what it actually offers. The legacy
// PricingComparisonTable file still lives in this folder for historical
// reference; it's not imported anywhere active. If a real feature-grid
// view is needed in the future, rebuild it by zipping the `highlights`
// arrays into rows.
